/**
 * Special Enrollment Period (SEP) Form Component
 *
 * This form checks if a user is in their Special Enrollment Period for Initial enrollment
 * based on their creditable coverage loss date.
 *
 * Usage:
 * - Add this component to your page with a title and optional disclaimer
 * - The component handles the entire SEP eligibility check flow
 * - Results are saved to Redux store (eligibilitySlice.sep)
 *
 * Flow:
 * 1. User selects if they lost coverage (last 2 months) or will lose it (next 3 months)
 * 2. User enters the date they lost/will lose coverage
 * 3. System validates the date is within the appropriate range
 * 4. System calculates enrollment window (3 months before to 2 months after loss date)
 * 5. Shows eligibility status and enrollment end date
 *
 * SEP Rules:
 * - Enrollment window: 3 months before loss of coverage to 2 months after
 * - User must have lost creditable coverage through employer, union, or COBRA
 */

'use client';

import { handleSEPSubmit } from 'actions/SEP';
import { useActionState, useEffect, useState } from 'react';
import { ComponentFields } from 'types/content';
import FormError from './FormError';
import { useDispatch } from 'react-redux';
import { setSEPEligibility, setEligibilityCase } from 'slices/eligibilitySlice';
import { useRouter } from 'next/navigation';
import { useAppSelector } from 'lib/hooks';
import { selectCurrentLocation } from 'slices/locationSlice';
import {
  determineMedicareSupEligibility,
  convertToEligibilityCase,
  EligibilityInput,
} from 'utils/medicareSupEligibility';
import { medSuppSlug } from 'lib/routing/medSuppSlug';
import { parseAudio } from 'lib/content-parsers/componentParsers';
import { usePlayAudio } from 'hooks/usePlayAudio';
import { selectBirthdate } from 'slices/selectors';
import { Audio } from 'kontent-ai/content-types';
import { SEPForm as SEPFormContent } from 'kontent-ai/content-types/sep_form';
import { ANALYTICS_EVENTS, capture } from 'lib/analytics';

const SEPForm = ({
  data,
  onContinue,
  setCurrentAudioSource,
  setCurrentAudioType,
  setCurrentAudioCaptions,
}: ComponentFields) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const location = useAppSelector(selectCurrentLocation);
  const userBirthday = useAppSelector(selectBirthdate);
  const playAudio = usePlayAudio({
    setCurrentAudioSource,
    setCurrentAudioType,
    setCurrentAudioCaptions,
  });

  const [coverageStatus, setCoverageStatus] = useState<
    'lost' | 'losing' | null
  >(null);

  const [state, formAction, isPending] = useActionState(handleSEPSubmit, {
    data: new FormData(),
    errors: {},
    results: null,
    success: false,
  });

  useEffect(() => {
    if (state.success && state.sepData) {
      // Save to Redux store
      dispatch(setSEPEligibility(state.sepData));

      capture(ANALYTICS_EVENTS.sepFormCompleted, {
        is_eligible: state.sepData.isEligible,
        coverage_status: state.sepData.coverageStatus,
      });

      // Continue to next page
      onContinue();
    }
    // onContinue is intentionally omitted: parent recreates it after success
    // (e.g. response audio), which would re-fire sep_form_completed.
  }, [state.success, state.sepData, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCoverageSelection = (status: 'lost' | 'losing') => {
    setCoverageStatus(status);

    const audioField =
      status === 'lost'
        ? (data as SEPFormContent).elements.lostCoverageAudio
        : (data as SEPFormContent).elements.losingAudioCoverage;

    playAudio(parseAudio(audioField?.linkedItems?.[0] as Audio), 'step');
  };

  const handleNo = () => {
    const audioField = (data as SEPFormContent).elements.noCoverageAudio;
    playAudio(parseAudio(audioField?.linkedItems?.[0] as Audio));

    const eligibilityInput: EligibilityInput = {
      state: location.state,
      userBirthday,
      currentDate: new Date(),
      hasQualifyingLifeEvent: false, // User said "No" to SEP question
      hasExistingMedSupp: false,
      isInAnotherWindowOfferingPartD: false,
    };

    // Determine Medicare eligibility
    const eligibilityResult = determineMedicareSupEligibility(eligibilityInput);

    // Convert to eligibility case for routing
    const eligibilityCase = convertToEligibilityCase(
      eligibilityResult,
      eligibilityInput
    );

    // Set eligibility case in Redux
    dispatch(setEligibilityCase(eligibilityCase));

    capture(ANALYTICS_EVENTS.sepFormCompleted, {
      is_eligible: false,
      coverage_status: null,
      answer: 'no',
    });

    router.push(`/${medSuppSlug(eligibilityCase)}`);
  };

  return (
    <section className="form-container">
      <form action={formAction}>
        {/* Initial Question */}
        {!coverageStatus && (
          <fieldset className="single-fieldset">
            <legend className="question-text">
              Have you lost creditable coverage through an employer, union, or
              COBRA within the last 2 months, or are you going to lose it within
              the next 3 months?
            </legend>
            <div className="buttons-container">
              <button
                type="button"
                className="button secondary choice"
                onClick={() => handleCoverageSelection('lost')}
              >
                I lost it within the last 2 months
              </button>
              <button
                type="button"
                className="button secondary choice"
                onClick={() => handleCoverageSelection('losing')}
              >
                I&apos;m going to lose it within 3 months
              </button>
              <button
                type="button"
                className="button secondary choice"
                onClick={handleNo}
              >
                No
              </button>
            </div>

            {/* error message*/}
            {state.errors.coverageStatus && (
              <FormError error={state.errors.coverageStatus} />
            )}
          </fieldset>
        )}

        {/* Optional Date Question (if have lost/going to lose coverage) */}
        {coverageStatus && (
          <fieldset className="single-fieldset">
            <label htmlFor="lossDate">
              <p className="question-text">
                {coverageStatus === 'lost'
                  ? 'When did you lose coverage?'
                  : 'When will you be losing coverage?'}
              </p>
              <p className="field-label">
                {coverageStatus === 'lost'
                  ? 'Please enter a date within the last 2 months'
                  : 'Please enter a date within the next 3 months'}
              </p>
            </label>
            <input type="hidden" name="coverageStatus" value={coverageStatus} />
            <input
              id="lossDate"
              type="date"
              name="lossDate"
              className="half"
              required
            />
            {state.errors.lossDate && (
              <FormError error={state.errors.lossDate} />
            )}

            <div className="buttons-container two-column">
              <button
                className="button primary"
                type="submit"
                disabled={isPending}
              >
                Submit
              </button>

              <button
                type="button"
                className="button secondary"
                onClick={() => setCoverageStatus(null)}
              >
                Back
              </button>
            </div>

            {isPending && <div className="spinner"></div>}

            {state.errors.api && <FormError error={state.errors.api} />}
          </fieldset>
        )}
      </form>

      {data.elements.disclaimer?.value && (
        <div className="disclaimer">{data.elements.disclaimer.value}</div>
      )}
    </section>
  );
};

export default SEPForm;
